import { Location } from '@angular/common';
import { inject } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    CanActivateFn,
    GuardResult,
    MaybeAsync,
    RedirectCommand,
    Router,
} from '@angular/router';
import { licenceRequired } from '../license.routes';
import {
    LicenseRestrictedOperation,
    LicenseRestrictedOperationContext,
} from '../models/license-restricted-operation.model';
import { LicenseRestrictionService } from '../services/license-restriction.service';
import { licenseUpgradeQueryParams } from '../utils/license-upgrade-route.util';

/**
 * Blocks a route reserved to the ADVANCED licence and lands the user on the
 * upgrade-request page, which reads the operation and its references back from
 * the query params to pick its copy and to file the request.
 *
 * `skipLocationChange` keeps the address bar off the block page, and the attempted
 * URL is written there instead, so a reload retries the real destination once the
 * licence changes. A pasted URL is already the attempted one; an in-app click is
 * not, because a refused navigation never commits its own URL.
 */
export const licenseUpgradeGuard = (
    operation: LicenseRestrictedOperation,
    contextFn?: (
        route: ActivatedRouteSnapshot,
    ) => LicenseRestrictedOperationContext | undefined,
    /**
     * Translation key of the blocked destination's own heading, so the block page
     * still says where the user was going.
     */
    pageTitle?: string,
): CanActivateFn => {
    return (route, state): MaybeAsync<GuardResult> => {
        const licenseRestrictionService = inject(LicenseRestrictionService);
        const router = inject(Router);
        const location = inject(Location);

        if (!licenseRestrictionService.isRestricted()) return true;

        location.replaceState(state.url);

        return new RedirectCommand(
            router.createUrlTree([licenceRequired], {
                queryParams: licenseUpgradeQueryParams(
                    operation,
                    contextFn?.(route),
                    undefined,
                    pageTitle,
                ),
            }),
            { skipLocationChange: true },
        );
    };
};
